import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  createWebhook,
  getWebhook,
  getWebhookDeliveries,
  updateWebhook,
  WEBHOOK_EVENT_OPTIONS,
  WebhookDelivery,
} from "../../../../helpers/webhooks_helper";

type FormValues = {
  url: string;
  events: string[];
  is_active: boolean;
};

/**
 * Criar / editar endpoint de webhook + rotacionar secret (DOC 06 § 6.9).
 * Secret plano só nesta resposta — redireciona para a lista com alerta.
 */
const WebhookCreateEdit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const webhookId = isNew ? null : Number(id);
  const locale = i18n.language || "pt-BR";

  document.title = isNew
    ? t("settings.webhooks.documentTitleNew")
    : t("settings.webhooks.documentTitleEdit");

  const helpHref = helpUrlForRoute("/settings/webhooks", locale);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [wasActive, setWasActive] = useState(true);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        url: Yup.string()
          .trim()
          .required(t("settings.webhooks.form.urlRequired"))
          .url(t("settings.webhooks.form.urlInvalid"))
          .test(
            "https",
            t("settings.webhooks.form.urlHttps"),
            (v) => !v || v.startsWith("https://")
          )
          .max(2048),
        events: Yup.array()
          .of(Yup.string())
          .min(1, t("settings.webhooks.form.eventsRequired")),
        is_active: Yup.boolean(),
      }),
    [t]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      url: "",
      events: ["message.sent", "message.failed"],
      is_active: true,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setError(null);
      try {
        if (isNew) {
          const res = await createWebhook({
            url: values.url.trim(),
            events: values.events,
            is_active: values.is_active,
          });
          toast.success(res?.message || t("settings.webhooks.toast.created"), {
            position: "top-center",
          });
          navigate("/settings/webhooks", {
            state: { revealedSecret: res.data?.secret },
          });
          return;
        }

        if (webhookId == null || Number.isNaN(webhookId)) {
          setError(t("settings.webhooks.error.load"));
          return;
        }

        // Pausar com eventos inscritos: confirmação explícita (anti-viés).
        if (wasActive && !values.is_active) {
          if (!window.confirm(t("settings.webhooks.pause.confirm"))) {
            return;
          }
        }

        const res = await updateWebhook(webhookId, {
          url: values.url.trim(),
          events: values.events,
          is_active: values.is_active,
        });
        toast.success(res?.message || t("settings.webhooks.toast.updated"), {
          position: "top-center",
        });
        navigate("/settings/webhooks");
      } catch (e) {
        setError(apiErrorMessage(e, t("settings.webhooks.error.save")));
      } finally {
        setSaving(false);
      }
    },
  });

  const loadDeliveries = useCallback(async () => {
    if (isNew || webhookId == null || Number.isNaN(webhookId)) {
      return;
    }
    setDeliveriesLoading(true);
    try {
      const res = await getWebhookDeliveries(webhookId);
      setDeliveries(res.data || []);
    } catch {
      setDeliveries([]);
    } finally {
      setDeliveriesLoading(false);
    }
  }, [isNew, webhookId]);

  const load = useCallback(async () => {
    if (isNew || webhookId == null || Number.isNaN(webhookId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getWebhook(webhookId);
      const row = res.data;
      if (!row) {
        setError(t("settings.webhooks.error.load"));
        return;
      }
      setWasActive(!!row.is_active);
      void formik.setValues({
        url: row.url,
        events: row.events?.length ? row.events : [],
        is_active: !!row.is_active,
      });
      await loadDeliveries();
    } catch (e) {
      setError(apiErrorMessage(e, t("settings.webhooks.error.load")));
    } finally {
      setLoading(false);
    }
    // formik estável o bastante via setValues; evita loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, webhookId, t, loadDeliveries]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEvent = (eventName: string) => {
    const current = formik.values.events;
    if (current.includes(eventName)) {
      void formik.setFieldValue(
        "events",
        current.filter((e) => e !== eventName)
      );
    } else {
      void formik.setFieldValue("events", [...current, eventName]);
    }
  };

  const onRotateSecret = async () => {
    if (webhookId == null || Number.isNaN(webhookId)) return;
    if (!window.confirm(t("settings.webhooks.rotate.confirm"))) {
      return;
    }
    setRotating(true);
    try {
      const res = await updateWebhook(webhookId, { rotate_secret: true });
      toast.success(res?.message || t("settings.webhooks.toast.rotated"), {
        position: "top-center",
      });
      navigate("/settings/webhooks", {
        state: { revealedSecret: res.data?.secret },
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("settings.webhooks.error.rotate")), {
        position: "top-center",
      });
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={
            isNew
              ? t("settings.webhooks.form.titleCreate")
              : t("settings.webhooks.form.titleEdit")
          }
          actions={
            <PageHelpLink
              href={helpHref}
              label={t("settings.webhooks.help")}
            />
          }
        />
        <ToastContainer />

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void formik.handleSubmit();
                    }}
                  >
                    <FormGroup>
                      <Label for="webhook-url">
                        {t("settings.webhooks.form.url")}
                      </Label>
                      <Input
                        id="webhook-url"
                        name="url"
                        type="url"
                        placeholder="https://hooks.suaempresa.com/brenvio"
                        value={formik.values.url}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={!!(formik.touched.url && formik.errors.url)}
                      />
                      {formik.touched.url && formik.errors.url && (
                        <FormFeedback>{formik.errors.url}</FormFeedback>
                      )}
                      <small className="text-muted">
                        {t("settings.webhooks.form.urlHint")}
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label>{t("settings.webhooks.form.events")}</Label>
                      <p className="text-muted small mb-2">
                        {t("settings.webhooks.form.eventsHint")}
                      </p>
                      {WEBHOOK_EVENT_OPTIONS.map((ev) => (
                        <div className="form-check mb-2" key={ev}>
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id={`ev-${ev}`}
                            checked={formik.values.events.includes(ev)}
                            onChange={() => toggleEvent(ev)}
                          />
                          <Label className="form-check-label" for={`ev-${ev}`}>
                            <strong>
                              {t(`settings.webhooks.events.${ev}`, {
                                defaultValue: ev,
                              })}
                            </strong>
                            <span className="d-block text-muted small">
                              {t(`settings.webhooks.eventsHint.${ev}`, {
                                defaultValue: "",
                              })}
                            </span>
                          </Label>
                        </div>
                      ))}
                      {formik.touched.events && formik.errors.events && (
                        <div className="text-danger small">
                          {String(formik.errors.events)}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup switch className="mb-4">
                      <Input
                        type="switch"
                        role="switch"
                        id="webhook-active"
                        checked={formik.values.is_active}
                        onChange={(e) =>
                          void formik.setFieldValue(
                            "is_active",
                            e.target.checked
                          )
                        }
                      />
                      <Label for="webhook-active" check>
                        {t("settings.webhooks.form.isActive")}
                      </Label>
                      <small className="d-block text-muted">
                        {t("settings.webhooks.form.isActiveHint")}
                      </small>
                    </FormGroup>

                    <div className="d-flex flex-wrap gap-2">
                      <Button color="primary" type="submit" disabled={saving}>
                        {saving ? (
                          <Spinner size="sm" />
                        ) : isNew ? (
                          t("settings.webhooks.form.submitCreate")
                        ) : (
                          t("settings.webhooks.form.submitSave")
                        )}
                      </Button>
                      <Button
                        color="light"
                        tag={Link}
                        to="/settings/webhooks"
                        disabled={saving}
                      >
                        {t("settings.webhooks.form.cancel")}
                      </Button>
                      {!isNew && (
                        <Button
                          color="warning"
                          outline
                          type="button"
                          className="ms-auto"
                          disabled={rotating || saving}
                          onClick={() => void onRotateSecret()}
                        >
                          {rotating ? (
                            <Spinner size="sm" />
                          ) : (
                            t("settings.webhooks.rotate.cta")
                          )}
                        </Button>
                      )}
                    </div>
                  </Form>
                </CardBody>
              </Card>

              {!isNew && (
                <Card className="mt-3">
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      {t("settings.webhooks.deliveries.title")}
                    </h5>
                    <Button
                      color="soft-secondary"
                      size="sm"
                      disabled={deliveriesLoading}
                      onClick={() => void loadDeliveries()}
                    >
                      {deliveriesLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        t("settings.webhooks.deliveries.refresh")
                      )}
                    </Button>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.webhooks.deliveries.hint")}
                    </p>
                    {deliveriesLoading && deliveries.length === 0 ? (
                      <div className="text-center py-3">
                        <Spinner color="primary" size="sm" />
                      </div>
                    ) : deliveries.length === 0 ? (
                      <p className="text-muted mb-0">
                        {t("settings.webhooks.deliveries.empty")}
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("settings.webhooks.deliveries.when")}</th>
                              <th>{t("settings.webhooks.deliveries.event")}</th>
                              <th>{t("settings.webhooks.deliveries.status")}</th>
                              <th>{t("settings.webhooks.deliveries.http")}</th>
                              <th>{t("settings.webhooks.deliveries.error")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deliveries.map((d) => (
                              <tr key={d.id}>
                                <td>
                                  {d.created_at
                                    ? new Date(d.created_at).toLocaleString(
                                        locale
                                      )
                                    : "—"}
                                </td>
                                <td>
                                  <code className="small">{d.event}</code>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      d.status === "delivered"
                                        ? "success"
                                        : d.status === "failed"
                                          ? "danger"
                                          : "secondary"
                                    }
                                  >
                                    {t(
                                      `settings.webhooks.deliveries.statusValue.${d.status}`,
                                      { defaultValue: d.status }
                                    )}
                                  </Badge>
                                  {d.attempts > 1 && (
                                    <span className="text-muted small ms-1">
                                      ×{d.attempts}
                                    </span>
                                  )}
                                </td>
                                <td>{d.last_http_status ?? "—"}</td>
                                <td
                                  className="text-break small text-muted"
                                  style={{ maxWidth: 220 }}
                                >
                                  {d.last_error || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default WebhookCreateEdit;
